/**
 * Shared between Organization and Staff views.
 * Not shared with Provider views (Provider Connections is a separate implementation).
 */

import { Status } from '../../../../../js/api';
import type { ConnectedFacility } from '../../../../../js/api';
import { FacilityDetails } from '../../../components/FacilityDetails';
import { StatusPill } from '../../../components/StatusPill';
import { StatusCodes, WorkflowScopes } from '../../../constants/workflows';
import { useCodeDefinitionsAsMaps } from '../../../hooks/useCodeDefinitions';

type OpportunityTabProps = {
  connection: ConnectedFacility;
  linkContext?: 'organization' | 'staff';
};

export function OpportunityTab({ connection, linkContext = 'organization' }: OpportunityTabProps) {
  const opportunity = connection.opportunity;
  const facilityId = connection.facility?.id;

  const { data: codeDefinitionMaps } = useCodeDefinitionsAsMaps(['FacilityTypes', 'Specialty']);

  if (!opportunity) {
    return (
      <div className="tw:text-sm tw:text-zinc-400 tw:italic tw:text-center">
        No opportunities associated with this connection.
      </div>
    );
  }

  return (
    <div className="tw:space-y-4">
      {/* Opportunity Link */}
      <section>
        <div className="tw:mt-2">
          <div className="tw:flex tw:items-center tw:justify-between tw:py-1 tw:border-b tw:border-zinc-100 tw:dark:border-zinc-700">
            <button
              type="button"
              onClick={() =>
                window.open(
                  `${linkContext === 'staff' ? '/staff/opportunities' : '/app/organization/opportunities'}?id=${opportunity.id}`,
                  '_blank',
                  'noopener,noreferrer'
                )
              }
              className="tw:text-sm tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:hover:underline tw:text-left tw:truncate tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0"
              title="View opportunity"
            >
              {opportunity.code || 'Untitled opportunity'}
            </button>
            <span className="tw:ml-2 tw:inline-flex tw:items-center">
              <StatusPill
                scope={WorkflowScopes.Opportunity}
                status={opportunity.current_status_id as StatusCodes | Status}
              />
            </span>
          </div>
        </div>
      </section>

      {/* Facility Details */}
      <FacilityDetails
        showDetails={true}
        opportunity={opportunity}
        facilityId={facilityId}
        codeDefinitions={codeDefinitionMaps}
      />
    </div>
  );
}
